import React from 'react';
import { Box, IconButton, Tooltip, CircularProgress } from '@mui/material';
import { Edit2, Trash, TickCircle } from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import { Task } from '../../types/task.type';
import { isTaskCompleted } from '../../utils/task.utils';

interface TaskRowActionsProps {
  task: Task;
  loading?: boolean;
  onToggleComplete: (task: Task) => void;
  onEdit: (task: Task) => void;
  onDelete: (task: Task) => void;
}

export default function TaskRowActions({
  task,
  loading,
  onToggleComplete,
  onEdit,
  onDelete,
}: TaskRowActionsProps) {
  const { t } = useTranslation();
  const isCompleted = isTaskCompleted(task);
  const completeColor = isCompleted ? '#16A34A' : '#64748B';
  const editColor = '#64748B';
  const deleteColor = '#64748B';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 32 }}>
        <CircularProgress size={16} />
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 0.25,
      minWidth: 108,
      overflow: 'visible',
    }}>
      <Tooltip title={isCompleted ? t('tasks.markIncomplete') : t('tasks.markComplete')}>
        <IconButton
          size="small"
          onClick={() => onToggleComplete(task)}
          sx={{
            width: 32,
            height: 32,
            '&:hover': { bgcolor: '#F0FDF4' },
          }}
        >
          <TickCircle size={18} color={completeColor} variant={isCompleted ? 'Bold' : 'Linear'} />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('tasks.editTask')}>
        <IconButton
          size="small"
          onClick={() => onEdit(task)}
          sx={{
            width: 32,
            height: 32,
            '&:hover': { bgcolor: '#EFF6FF' },
          }}
        >
          <Edit2 size={18} color={editColor} />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('tasks.deleteTask')}>
        <IconButton
          size="small"
          onClick={() => onDelete(task)}
          sx={{
            width: 32,
            height: 32,
            '&:hover': { bgcolor: '#FEF2F2' },
          }}
        >
          <Trash size={18} color={deleteColor} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
